<template>
  <div class="dss-cardio-medication" v-disable-all="hasReadOnlyAccess">
    <missing-d-s-s-data-popup v-model="missingDSSPopupMessage"/>
    <prescription-recently-updated-popup />

    <div class="dss__subheader">
      <div class="dss__subheader-main mb-8 mt-10">
        <smart-icon icon="mdi-heart-outline" size="16" class="mr-2"/>
        <h3>{{ $ucfirst($t("dss.currentPrescription")) }}</h3>
      </div>

      <div class="d-flex justify-end">
        <v-btn
            color="primary"
            right
            class="v-size--super add-drug-button"
            text
            @click="addMedication"
        >
          <eva-icon
              class="medication__icon mr-2"
              name="plus-circle-outline"
          />
          {{ $ucfirst($t('patient.addDrug')) }}
        </v-btn>
      </div>
    </div>

    <duplicated-class-popup
      :value="showDialog"
      :duplicatedClasses="duplicatedClasses"
      @close="showDialog = false"
      @handleSubmit="submitMedication(editingMedication, editingIndex, editingMedicationComponent)"
    />

    <medication
        v-for="(cardioMedicationScheduleWithRecommendationInfo, index) in cardioMedicationSchedulesWithRecommendationInfo"
        ref="schedules"
        :key="`schedule-${cardioMedicationScheduleWithRecommendationInfo.medicationSchedule.id}`"
        :index="index"
        :value="cardioMedicationScheduleWithRecommendationInfo.medicationSchedule"
        :color="cardioMedicationScheduleWithRecommendationInfo.medicationSchedule.recommended === 2 ? 'green' : ''"
        :single-class="false"
        :medicationSchedulesWithRecommendationInfo="cardioMedicationScheduleWithRecommendationInfo"
        @input="(medicationSchedule, medicationComponent) => checkMedicationAndSubmit(medicationSchedule, index, medicationComponent)"
        @remove="(id) => removeMedication(id, index)"
    ></medication>

    <div class="scroll-to-end-medications" id="scroll-to-end-medications" />

    <div
        v-if="!cardioMedicationSchedulesWithRecommendationInfo.length && !showNewMedication"
        class="medication-list__empty mb-6"
    >{{ $ucfirst($t('patient.medicationListEmpty')) }}
    </div>
    <!-- Add New medication -->
    <medication
        v-if="showNewMedication"
        ref="newSchedule"
        :isNew="true"
        :value="newMedication"
        :single-class="false"
        :index="cardioMedicationSchedulesWithRecommendationInfo.length || 0"
        :medicationSchedulesWithRecommendationInfo="emptyMedicationSchedulesWithRecommendationInfo"
        :pulsing="newMedicationPulsing"
        @input="(medicationSchedule, medicationComponent) => checkMedicationAndSubmit(medicationSchedule, null, medicationComponent)"
        @remove="removeNewMedication"
    ></medication>

    <h3
        v-if="unhandledMedicationSchedulesWithRecommendationInfo.length"
        class="mb-6"
    >{{ $ucfirst($t("expertTool.recommendation.title")) }}</h3>

    <medication
        v-for="(unhandledMedicationScheduleWithRecommendationInfo, index) in unhandledMedicationSchedulesWithRecommendationInfo"
        ref="unhandleds"
        :key="`recommendation-${unhandledMedicationScheduleWithRecommendationInfo.medicationRecommendations[0].id}`"
        :index="index"
        :value="unhandledMedicationScheduleWithRecommendationInfo.medicationSchedule"
        color="yellow"
        :single-class="false"
        :medicationSchedulesWithRecommendationInfo="unhandledMedicationScheduleWithRecommendationInfo"
        @input="(medicationSchedule, medicationComponent) => checkMedicationAndSubmit(medicationSchedule, index, medicationComponent)"
    ></medication>
    <unsaved-changes-warning
        ref="unsavedChangesWarning"
        screen="cardioMedication"
        :custom-leave-message="$t('medication.leaveScreen')"
    />
  </div>
</template>

<script>
import Medication from '../../components/Parts/Medication/Medication';
import SmartIcon from "../../components/Parts/SmartIcon.vue";
import gql from "graphql-tag";
import {mapGetters} from "vuex";
import MissingDSSDataPopup from "../../components/Parts/DSS/MissingDSSDataPopup";
import {isDistinct, isEqual, uniqueValues} from "../../../../helper/arrays";
import UnsavedChangesWarning from "../../components/Parts/UnsavedChangesWarning.vue";
import PrescriptionRecentlyUpdatedPopup from '../../components/Parts/DSS/PrescriptionRecentlyUpdatedPopup.vue';
import DuplicatedClassPopup from '../../components/Parts/DSS/DuplicatedClassPopup.vue';

/**
 * Component DSSCardioMedication.vue
 *
 * @module DSSCardioMedication
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.10] app/src/APP_DASHBOARD/templates/views/DSS/DSSCardioMedication.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: "DSSCardioMedication",
  components: {
    MissingDSSDataPopup,
    Medication,
    SmartIcon,
    UnsavedChangesWarning,
    PrescriptionRecentlyUpdatedPopup,
    DuplicatedClassPopup
  },
  created() {
    this.$app.eventBus.$on('DSSHeader.saveAndExit', this.saveAndExit)
  },
  mounted() {
    // before refreshing or leaving screen (app) completely
    // delete all queued recommendations
    window.addEventListener('beforeunload', this.rejectAllQueuedRecommendations)
  },
  beforeDestroy(){
    this.$app.eventBus.$off('DSSHeader.saveAndExit', this.saveAndExit);
    window.removeEventListener('beforeunload', this.rejectAllQueuedRecommendations);
  },
  computed: {
    ...mapGetters({
      patientId: 'patientSummary/id',
      cardioMedicationSchedulesWithRecommendationInfo: 'dss/cardioMedicationSchedulesWithRecommendationInfo',
      unhandledMedicationSchedulesWithRecommendationInfo: 'dss/unhandledMedicationSchedulesWithRecommendationInfo',
      algorithm: 'dss/algorithm',
      unsavedChanges: 'patientSummary/unsavedChanges',
      newMedication: 'dss/newMedication',
      schedulesToBeDeleted: 'dss/schedulesToBeDeleted',
      hasReadOnlyAccess: "user/hasReadOnlyAccess"
    }),
    missingDSSPopupMessage: {
      get() {
        return this.$store.state.dss.missingDSSPopupMessage
      },
      set(value) {
        this.$store.state.dss.missingDSSPopupMessage = value
      }
    },
    emptyMedicationSchedulesWithRecommendationInfo() {
      return {
        guidelines: [],
        isUnhandled: false,
        medicationRecommendations: [],
        medicationSchedule: this.newMedication,
        recommendedClasses: []
      }
    }
  },
  data() {
    return {
      doubleMedicationClassCheckEnabled : true,
      showDialog: false,
      editingMedication: null,
      editingIndex: null,
      editingMedicationComponent: null,
      duplicatedClasses: null,
      showNewMedication: false,
      newMedicationPulsing: false
    };
  },
  methods: {
    saveAndExit(withoutSaving) {

      if (withoutSaving) {
        // if there are any unsaved changes, the confirmation about leaving will be triggered
        this.$router.push({name: 'DSSMedicationPrescription'})
        return;
      }

      // for now disable double medication check for global save
      this.doubleMedicationClassCheckEnabled = false;

      // Dialog for showing that the user cannot leave if there are still yellow rows
      let cannotLeaveDialog = false;

      // Post existing medication prescriptions
      this.cardioMedicationSchedulesWithRecommendationInfo.forEach((medicationScheduleOrRecommendation, index) => {
        const medicationComponent = this.$refs['schedules'][index];

        if (medicationComponent.isEditable){
          medicationComponent.submit();
          if (!medicationComponent.isValid){
            cannotLeaveDialog = true;
          }
        }
      })

      // Post new medication prescription
      const medicationComponentNew = this.$refs['newSchedule'];
      if (medicationComponentNew && medicationComponentNew.isEditable){
        medicationComponentNew.submit();
        if (!medicationComponentNew.isValid){
          cannotLeaveDialog = true;
        }
      }

      // Try to post recommendations
      this.unhandledMedicationSchedulesWithRecommendationInfo.forEach((medicationScheduleOrRecommendation, index) => {
        const medicationComponent = this.$refs['unhandleds'][index];

        if (medicationComponent.isEditable) {
          medicationComponent.submit();
          if (!medicationComponent.isValid) {
            cannotLeaveDialog = true;
          }
        }else{
          cannotLeaveDialog = true;
        }
      })

      this.$app.eventBus.$emit('DSS.setCannotLeaveDialog', cannotLeaveDialog);

      if (!cannotLeaveDialog){
        this.$router.push({name: 'DSSMedicationPrescription'})
      }else{
        this.doubleMedicationClassCheckEnabled = true;
      }
    },
    addMedication() {
      this.showNewMedication = true

      setTimeout(() => {
        this.newMedicationPulsing = true;
        document.getElementById('scroll-to-end-medications').scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200)

      setTimeout(() => {
        this.newMedicationPulsing = false;
      }, 2000)
    },
    async submitMedication(medication, index, medicationComponent) {
      this.showDialog = false;
      this.duplicatedClasses = null;
      const isNew = medicationComponent._props.isNew;

      // Check if a medication class already has been added
      const success = await this.$store
        .dispatch('dss/submitDSSMedicationSchedule', {medication, index});

      if (success) {
        medicationComponent.setIsEditable(false);
        this.editingIndex = null;
        this.editingMedication = null;
        this.$toasted.show(this.$ucfirst(this.$t('dss.prescriptionUpdated')), {
          type: 'success',
          position: 'bottom-center',
        });

        if (isNew) {
          this.showNewMedication = false;
        }

        const id = medication.id
          ? medication.id
          : medicationComponent.recommendationId
            ? medicationComponent.recommendationId
            : isNew
              ? 'new'
              : index;

        this.toggleUnsaved(id, false)
      }
    },
    async removeMedication(id, index) {
      const success = await this.$store
          .dispatch('dss/removeMedicationSchedule', {id, index});

      if (success) {
        this.$toasted.show(this.$ucfirst(this.$t('dss.prescriptionRemoved')), {
          type: 'success',
          position: 'bottom-center',
        });

        this.toggleUnsaved(id ? id : index, false)
      }
    },
    deleteCardioMedication(id) {
      this.$app.apolloMobileClient
          .mutate({
            mutation: gql`
            mutation deleteMyMedicationSchedule($id: String!){
              deleteMyMedicationSchedule(id : $id)
            }
          `,
            variables: {
              id
            }
          })
          .then(result => {
            console.log('DELETED', result);
          })
    },
    removeNewMedication() {
      this.showNewMedication = false;
      this.toggleUnsaved('new', false)
    },
    checkMedicationAndSubmit(medication, index, medicationComponent) {
      this.editingMedication = medication;
      this.editingIndex = index;
      this.editingMedicationComponent = medicationComponent;

      if (!this.doubleMedicationClassCheckEnabled || !this.isMedicationClassAlreadyAdded(medication)) {
        this.submitMedication(medication, index, medicationComponent);
      } else {
        this.showDialog = true;
      }
    },
    isMedicationClassAlreadyAdded(medication) {
      let hasAlreadyBeenAdded = false;
      let duplicatedClasses = [];
      this.cardioMedicationSchedulesWithRecommendationInfo.forEach((cardioMedicationScheduleWithRecommendationInfo) => {
        const medicationSchedule = cardioMedicationScheduleWithRecommendationInfo.medicationSchedule;
        // Check if a prescription line has duplicate classes, shouldn't be possible anymore, unless manipulated
        if (!isDistinct(medication.classes)) {
          hasAlreadyBeenAdded = true;
        } else {
          // Don't check if a saved prescription is already in saved schedule, else it could be a duplicate
          // But check prescription if it is a new one.
          if (medicationSchedule.id !== medication.id) {
            medicationSchedule.medication.classes.forEach((medicationClass) => {
              // Check For Duplicate Class
              const hasDuplicate = medication.classes.some((newMedicationClass) => medicationClass.key === newMedicationClass);
              if (hasDuplicate && !duplicatedClasses.includes(medicationClass.name)) {
                duplicatedClasses.push(medicationClass.name);
              }
              // Break out
              // return !hasAlreadyBeenAdded;
            });
          }
          hasAlreadyBeenAdded = !!duplicatedClasses.length
        }
        // Break out
        // return !hasAlreadyBeenAdded;
      });
      this.duplicatedClasses = duplicatedClasses.join(', ')
      return hasAlreadyBeenAdded;
    },
    toggleUnsaved(id, unsaved) {
      const data = {
        unsavedChanges: unsaved,
        screen: 'cardioMedication',
        name: `medication-${id}`
      }
      this.$store.dispatch('patientSummary/toggleMultipleUnsavedChanges', data);
    },
    async rejectAllQueuedRecommendations() {
      const medicationComponents = this.$refs['unhandleds'];
      // get data from child components
      const softRejects = medicationComponents.map((item, index) => {
        return {
          index,
          id: item.softDeletedId,
          timeout: item.timeOutDelete,
          rejection: item.rejectionDetails
        }
      }).filter(item => item.id);
      if (!softRejects.length) return;

      const rejects = softRejects.map(item => {
        return {
          medicationRecommendationId: item.rejection.id,
          types: item.rejection.data,
          other: item.rejection.otherText,
        }
      })

      const rejected = await this.$store.dispatch('dss/rejectMultipleRecommendations', rejects);

      if (rejected) {
        this.$store.dispatch('dss/setSchedulesToBeDeleted', {add:false, reset:true})
        this.$store.dispatch('dss/getDSSMedicationSchedules');
      }

      // trigger clearTimeout in child components
      softRejects.forEach(item => {
        medicationComponents[item.index].clearTimeoutDelete();
      })
      this.$toasted.clear();
    }
  },
  async beforeRouteLeave(to, from, next) {
    if (this.unsavedChanges('cardioMedication')) {
      this.$refs.unsavedChangesWarning.prepareToExit(to.name);
      return false;
    }
    // reject all queued recommendations before leaving route
    if (this.schedulesToBeDeleted > 0) {
      await this.rejectAllQueuedRecommendations();
    }
    next();
  },
};
</script>

<style lang="scss">
.medication-list__empty {
  background-color: #f6f6f6;
  border-radius: 16px;
  width: 100%;
  height: 282px;
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: bold;
}

.medication__icon {
  height: 24px;
}

.add-drug-button{
  border: 1px solid #3D0A5E;
}

.scroll-to-end-medications{
  visibility: hidden;
  pointer-events: none;
  position: relative;
  top: -200px;
}
</style>
